import stringifyGraphQlVars from 'src/internal/prefetching/stringifyGraphQlVars';
import {MAX_STORE_SIZE} from 'src/internal/revamp/config/createGraphQlDataClient';
import {getFieldNodesWithSpreadFragmentFields} from 'src/internal/revamp/graphql/documentUtils/entrypoint';
import ObservableFragment from 'src/internal/revamp/observable/ObservableFragment';
import ObservableQuery from 'src/internal/revamp/observable/ObservableQuery';
import {generateHashForDocument} from 'src/internal/revamp/queryUtils/documentHashing';
import {buildQueryAndVariablesHash} from 'src/internal/revamp/queryUtils/queryAndVariablesHashing';
import selectRootQueryRefetches from 'src/internal/revamp/queryUtils/selectRootQueryRefetches';
import {reportRuntimeError} from 'src/internal/revamp/telemetry/internalReporting';

import type {
  PerformanceMonitorTrackStart,
  PerformanceMonitorTrackStop,
} from '@sail/observability';
import type {DocumentNode, FieldNode} from 'graphql';
import type ApolloLinkExecutor from 'src/internal/revamp/apollo/ApolloLinkExecutor';
import type MemoryCache from 'src/internal/revamp/cache/MemoryCache';
import type {
  InvalidateTopLevelFieldsOptions,
  ValidTopLevelField,
} from 'src/internal/revamp/graphql/types';
import type ResultStore from 'src/internal/revamp/observable/ResultStore';
import type PollingClock from 'src/internal/revamp/polling/PollingClock';
import type {
  RootQueryParams,
  RootQueryParamsMatch,
} from 'src/internal/revamp/queryUtils/selectRootQueryRefetches';
import type {FragmentHashWithVariables} from 'src/internal/revamp/queryUtils/types';
import type {
  GraphQlDocument,
  VariablesOfDoc,
} from 'src/internal/shared/graphql/types/document';
import type {ObservabilityFns} from 'src/internal/shared/observability/types';

function evictOldest(map: Map<string, any>): void {
  const oldest = map.keys().next().value;

  if (oldest) {
    map.delete(oldest);
  }
}

export type RefetchFragmentsFn = (
  fragments: ReadonlyArray<FragmentHashWithVariables>,
) => void;

// null means wildcard invalidation; {} is a concrete fragment with no variables.
// They must not alias, or invalidation could remove a suspended retry's executor.
const fragmentRefetchKey = ({
  hash,
  variables,
}: FragmentHashWithVariables): string =>
  JSON.stringify([hash, stringifyGraphQlVars(variables)]);

export default class ObservableStore {
  private observableQueryMap: Map<string, ObservableQuery<any>>;
  private observableFragmentMap: Map<string, ObservableFragment<any>>;

  private rootQueryRefetchDataMap: Map<
    string,
    {
      rootQuery: DocumentNode;
      refetchFragments: RefetchFragmentsFn;
      includedFragmentHashes: ReadonlyArray<string>;
      // Each registration is a distinct object, even when composition keys match.
      registrations: Set<{compositionKey: string}>;
    }
  >;

  // A shared Suspense boundary can prevent the runner's registration effect from
  // ever committing. Failed requests therefore retain an executor for the exact
  // fragment + variables until that Suspense result settles or a committed runner
  // takes over, including when the original runner unmounts before its replacement
  // commits.
  // These executors never participate in top-level invalidation.
  // At capacity, fetch the oldest retry immediately when evicting its executor.
  private suspendedFragmentRetries = new Map<
    string,
    {fragment: FragmentHashWithVariables; refetchFragments: RefetchFragmentsFn}
  >();

  // Params are indexed by document, then composition document + root variables.
  // Renders update them so retries can read fresh params during suspension,
  // before effects commit.
  private rootQueryParamsMap: Map<string, Map<string, RootQueryParams>> =
    new Map();

  // Applies MAX_STORE_SIZE across all document+variables entries. The nested
  // map above supports document-scoped lookup; this map owns global recency and
  // prevents each document from retaining MAX_STORE_SIZE entries independently.
  private rootQueryParamsOrderMap: Map<
    string,
    {documentKey: string; compositionKey: string}
  > = new Map();

  private topLevelFieldsToObservableHashesMap: Map<string, Set<string>>;

  private apolloLinkExecutor: ApolloLinkExecutor;

  private resultStore: ResultStore;

  private observabilityFns: ObservabilityFns;

  private memoryCache: MemoryCache;

  private pollingClock: PollingClock;

  // Batching state for invalidateQueries
  private pendingTopLevelFields: Set<ValidTopLevelField> = new Set();
  private pendingPurgeTopLevelFields: Set<ValidTopLevelField> = new Set();
  private isBatchScheduled = false;

  // Batching state for refetchSingleFragment
  private pendingFragmentRefetches: Map<string, FragmentHashWithVariables> =
    new Map();

  private isFragmentBatchScheduled = false;

  constructor(
    observabilityFns: ObservabilityFns,
    apolloLinkExecutor: ApolloLinkExecutor,
    resultStore: ResultStore,
    memoryCache: MemoryCache,
    pollingClock: PollingClock,
  ) {
    this.observabilityFns = observabilityFns;
    this.apolloLinkExecutor = apolloLinkExecutor;
    this.resultStore = resultStore;
    this.memoryCache = memoryCache;
    this.pollingClock = pollingClock;

    this.observableQueryMap = new Map();
    this.observableFragmentMap = new Map();
    this.rootQueryRefetchDataMap = new Map();
    this.topLevelFieldsToObservableHashesMap = new Map();
  }

  public getObservableFragment<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >({
    document,
    variables,
  }: {
    document: TDocumentNode;
    variables: VariablesOfDoc<TDocumentNode> | undefined;
  }): ObservableFragment<TDocumentNode> | undefined {
    const {documentHashWithVariables} = buildQueryAndVariablesHash(
      document,
      variables,
    );

    return this.observableFragmentMap.get(documentHashWithVariables);
  }

  public getObservableQuery<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >({
    document,
    variables,
  }: {
    document: TDocumentNode;
    variables: VariablesOfDoc<TDocumentNode> | undefined;
  }): ObservableQuery<TDocumentNode> | undefined {
    const {documentHashWithVariables} = buildQueryAndVariablesHash(
      document,
      variables,
    );

    return this.observableQueryMap.get(documentHashWithVariables);
  }

  public createObservableFragment<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >({
    document,
    variables,
  }: {
    document: TDocumentNode;
    variables: VariablesOfDoc<TDocumentNode> | undefined;
  }): ObservableFragment<TDocumentNode> {
    const observable = this.getObservableFragment({document, variables});

    if (observable) {
      return observable;
    }

    const observableFragment = new ObservableFragment(
      document,
      variables,
      this.resultStore,
      this.pollingClock,
    );
    const {documentHash, documentHashWithVariables} =
      buildQueryAndVariablesHash(document, variables);

    const documentTopLevelFields =
      getFieldNodesWithSpreadFragmentFields(document);

    this.addObservableFragment(documentHashWithVariables, observableFragment);
    this.registerObservableDataOnTopLevelFields(
      documentTopLevelFields,
      documentHash,
    );
    return observableFragment;
  }

  public createObservableQuery<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >({
    document,
    variables,
    trackStart,
    trackStop,
  }: {
    document: TDocumentNode;
    variables: VariablesOfDoc<TDocumentNode> | undefined;
    trackStart?: PerformanceMonitorTrackStart | null;
    trackStop?: PerformanceMonitorTrackStop | null;
  }): ObservableQuery<TDocumentNode> {
    const observable = this.getObservableQuery({document, variables});

    if (observable) {
      observable.setTrackStart(trackStart ?? null);
      observable.setTrackStop(trackStop ?? null);
      return observable;
    }
    const {documentHash, documentHashWithVariables} =
      buildQueryAndVariablesHash(document, variables);

    const observableQuery = new ObservableQuery(
      document,
      variables,
      this.resultStore,
      this.pollingClock,
      this.memoryCache,
      this.apolloLinkExecutor,
      this.observabilityFns,
      trackStart ?? null,
      trackStop ?? null,
    );

    const documentTopLevelFields =
      getFieldNodesWithSpreadFragmentFields(document);

    this.addObservableQuery(documentHashWithVariables, observableQuery);
    this.registerObservableDataOnTopLevelFields(
      documentTopLevelFields,
      documentHash,
    );

    return observableQuery;
  }

  public registerRootQuery({
    document,
    compositionKey,
    refetchFragments,
    includedFragmentHashes,
  }: {
    document: DocumentNode;
    compositionKey: string;
    refetchFragments: RefetchFragmentsFn;
    includedFragmentHashes: ReadonlyArray<string>;
  }): () => void {
    const documentKey = generateHashForDocument(document);
    const registration = {compositionKey};
    let entry = this.rootQueryRefetchDataMap.get(documentKey);
    if (entry) {
      // Request params are selected by fragment variables. The callback's
      // expected-error and attribution context still follows the last writer.
      entry.refetchFragments = refetchFragments;
      entry.includedFragmentHashes = includedFragmentHashes;
    } else {
      entry = {
        rootQuery: document,
        refetchFragments,
        includedFragmentHashes,
        registrations: new Set(),
      };
      this.rootQueryRefetchDataMap.set(documentKey, entry);
    }
    entry.registrations.add(registration);

    return () => {
      if (!entry.registrations.delete(registration)) {
        return;
      }
      if (entry.registrations.size === 0) {
        this.rootQueryRefetchDataMap.delete(documentKey);
      }
      // Keep render-time params: a suspended render may have refreshed them
      // without committing. The params store bounds their retention separately.
    };
  }

  public registerSuspendedFragmentRetry(
    fragment: FragmentHashWithVariables,
    refetchFragments: RefetchFragmentsFn,
    until: Promise<void>,
  ): void {
    const key = fragmentRefetchKey(fragment);
    const entry = {fragment, refetchFragments};
    // A later failed attempt replaces the executor for this pair and refreshes
    // its eviction order. Promise cleanup below must only remove its own entry.
    this.suspendedFragmentRetries.delete(key);
    this.suspendedFragmentRetries.set(key, entry);

    if (this.suspendedFragmentRetries.size > MAX_STORE_SIZE) {
      const oldest = this.suspendedFragmentRetries.entries().next().value;
      if (oldest) {
        const [oldestKey, oldestEntry] = oldest;
        this.suspendedFragmentRetries.delete(oldestKey);
        // Retry attempts advance on responses, not elapsed time. Dropping the
        // only executor would strand Suspense, so fetch now instead of waiting
        // for its scheduled retry to find an entry that no longer exists.
        // That response can register a replacement if the fragment still retries.
        oldestEntry.refetchFragments([oldestEntry.fragment]);
      }
    }

    const cleanup = (): void => {
      // An older request's completion must not remove a newer retry executor.
      if (this.suspendedFragmentRetries.get(key) === entry) {
        this.suspendedFragmentRetries.delete(key);
      }
    };
    // This is the consumer's whole Suspense wait, which can span several network
    // attempts. Unmount is not a reliable cleanup point: the runner may never
    // have committed, and an abandoned render may still have retries in progress.
    until.then(cleanup, cleanup);
  }

  /**
   * Stores the current params for a composition document and its root variables.
   * Called during render to ensure retry callbacks can read fresh params.
   * Safe to call during render as this is an external store mutation.
   */
  public setRootQueryParams(
    rootQuery: DocumentNode,
    params: RootQueryParams,
  ): void {
    const documentKey = generateHashForDocument(rootQuery);
    let paramsByRootQuery = this.rootQueryParamsMap.get(documentKey);

    if (!paramsByRootQuery) {
      paramsByRootQuery = new Map();
      this.rootQueryParamsMap.set(documentKey, paramsByRootQuery);
    }

    paramsByRootQuery.delete(params.compositionKey);
    paramsByRootQuery.set(params.compositionKey, params);

    const paramsOrderKey = JSON.stringify([documentKey, params.compositionKey]);
    this.rootQueryParamsOrderMap.delete(paramsOrderKey);
    this.rootQueryParamsOrderMap.set(paramsOrderKey, {
      documentKey,
      compositionKey: params.compositionKey,
    });

    if (this.rootQueryParamsOrderMap.size > MAX_STORE_SIZE) {
      this.evictOldestRootQueryParams();
    }
  }

  private evictOldestRootQueryParams(): void {
    const oldestEntry = this.rootQueryParamsOrderMap.entries().next().value;

    if (!oldestEntry) {
      return;
    }

    const [orderKey, {documentKey, compositionKey}] = oldestEntry;
    this.rootQueryParamsOrderMap.delete(orderKey);

    const paramsByRootQuery = this.rootQueryParamsMap.get(documentKey);
    paramsByRootQuery?.delete(compositionKey);

    if (paramsByRootQuery?.size === 0) {
      this.rootQueryParamsMap.delete(documentKey);
    }
  }

  // Normal lookup can rebuild retries from render-time params even before commit.
  // mountedOnly instead asks whether a committed runner covers the requested pair;
  // using a render-only match for that decision would suppress its fallback executor.
  public getMatchingRootQueryParams(
    rootQuery: DocumentNode,
    fragments: ReadonlyArray<FragmentHashWithVariables>,
    {
      mountedOnly = false,
      retainedParams,
    }: {mountedOnly?: boolean; retainedParams?: RootQueryParams} = {},
  ): ReadonlyArray<RootQueryParamsMatch> {
    if (fragments.length === 0) {
      return [];
    }
    const documentKey = generateHashForDocument(rootQuery);
    const paramsByRootQuery = this.rootQueryParamsMap.get(documentKey);

    const mountedCompositionKeys = new Set(
      Array.from(
        this.rootQueryRefetchDataMap.get(documentKey)?.registrations ?? [],
        ({compositionKey}) => compositionKey,
      ),
    );
    const candidates = Array.from(paramsByRootQuery?.values() ?? []).reverse();
    // Add the failed request's snapshot only if its composition's stored params
    // were evicted. A present but non-matching entry is intentional: e.g. a newer
    // render now skips User, so the old snapshot must not restore that fragment.
    if (
      !mountedOnly &&
      retainedParams &&
      !paramsByRootQuery?.has(retainedParams.compositionKey)
    ) {
      // Keep the snapshot behind every current render, even one from another
      // composition that can cover these fragment variables.
      candidates.push(retainedParams);
      // Demote it only for this lookup; do not unregister the actual runner.
      // An old registration cannot make evicted params current or allow this
      // snapshot to participate in wildcard invalidation.
      mountedCompositionKeys.delete(retainedParams.compositionKey);
    }

    return selectRootQueryRefetches(
      mountedOnly
        ? candidates.filter(({compositionKey}) =>
            mountedCompositionKeys.has(compositionKey),
          )
        : candidates,
      mountedCompositionKeys,
      fragments,
    );
  }

  public invalidateTopLevelFields(
    topLevelFields: ReadonlyArray<ValidTopLevelField>,
    options?: InvalidateTopLevelFieldsOptions,
  ): void {
    topLevelFields.forEach((field) => this.pendingTopLevelFields.add(field));

    if (options?.purgeCache) {
      topLevelFields.forEach((field) =>
        this.pendingPurgeTopLevelFields.add(field),
      );
    }

    if (!this.isBatchScheduled) {
      this.isBatchScheduled = true;
      setTimeout(() => {
        this.processPendingInvalidations();
      }, 0);
    }
  }

  private processPendingInvalidations(): void {
    if (this.pendingTopLevelFields.size === 0) {
      this.isBatchScheduled = false;
      return;
    }

    const topLevelFields = Array.from(this.pendingTopLevelFields);
    const topLevelFieldsToPurge = Array.from(this.pendingPurgeTopLevelFields);
    this.pendingTopLevelFields.clear();
    this.pendingPurgeTopLevelFields.clear();
    this.isBatchScheduled = false;

    const queryHashes =
      this.getObservableHashesForTopLevelFields(topLevelFields);

    this.resultStore.invalidateQueriesByDocumentHash(queryHashes);

    const {observableFragmentHashes, observableQueries} =
      this.zipDocumentHashesPerObservableType(queryHashes);

    const observableFragmentHashesWithVariables = observableFragmentHashes.map(
      (hash) => ({hash, variables: null}),
    );

    this.triggerFragmentsRefetch(observableFragmentHashesWithVariables);
    triggerQueriesRefetch(observableQueries);

    if (topLevelFieldsToPurge.length > 0) {
      this.memoryCache.evictTopLevelFields(topLevelFieldsToPurge);
    }
  }

  public refetchSingleFragment<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >({
    document,
    variables,
  }: {
    document: TDocumentNode;
    variables: VariablesOfDoc<TDocumentNode> | undefined;
  }): void {
    // undefined variables get treated as empty object down the line.
    const finalVariables = variables || {};
    const {documentHash, documentHashWithVariables} =
      buildQueryAndVariablesHash(document, finalVariables);

    const fragment = this.observableFragmentMap.get(documentHashWithVariables);
    if (!fragment) {
      reportRuntimeError(
        this.observabilityFns,
        new Error('Fragment not found'),
        'Could not find a fragment in observable store',
      );
      return;
    }

    fragment.clearLastFetchedBy();

    this.pendingFragmentRefetches.set(documentHashWithVariables, {
      hash: documentHash,
      variables: finalVariables,
    });

    if (!this.isFragmentBatchScheduled) {
      this.isFragmentBatchScheduled = true;
      setTimeout(() => {
        this.processPendingFragmentRefetches();
      }, 0);
    }
  }

  private processPendingFragmentRefetches(): void {
    if (this.pendingFragmentRefetches.size === 0) {
      this.isFragmentBatchScheduled = false;
      return;
    }

    const fragmentsToRefetch = Array.from(
      this.pendingFragmentRefetches.values(),
    );

    this.pendingFragmentRefetches.clear();
    this.isFragmentBatchScheduled = false;

    this.triggerFragmentsRefetch(fragmentsToRefetch);
  }

  private zipDocumentHashesPerObservableType(hashes: ReadonlyArray<string>): {
    observableFragmentHashes: ReadonlyArray<string>;
    observableQueries: ReadonlyArray<ObservableQuery<any>>;
  } {
    const observableFragmentHashes = hashes.filter((hash) => {
      const activeEntryIncludingHash = Array.from(
        this.observableFragmentMap.entries(),
      ).find(([key, fragment]) => {
        return key.includes(hash) && fragment.isActive();
      });

      return typeof activeEntryIncludingHash !== 'undefined';
    });

    const observableQueries = Array.from(this.observableQueryMap.entries())
      .filter(([key, query]) => {
        return hashes.some((hash) => key.includes(hash)) && query.isActive();
      })
      .map(([_key, query]) => {
        return query;
      });
    return {observableFragmentHashes, observableQueries};
  }

  private triggerFragmentsRefetch(
    fragments: ReadonlyArray<FragmentHashWithVariables>,
  ): void {
    const rootQueryRefetchCallbacks = Array.from(
      this.rootQueryRefetchDataMap.values(),
    );

    const rootQueriesToRefetch = rootQueryRefetchCallbacks.filter(
      (rootQueryData) =>
        rootQueryData.includedFragmentHashes.find((fragmentHash) =>
          fragments.some((fragment) => fragment.hash === fragmentHash),
        ),
    );

    // Fallbacks are for explicit fragment + variables retries. A wildcard
    // invalidation must not restart work from a suspended or abandoned render.
    const retryCandidates = fragments.filter(
      (fragment) =>
        fragment.variables !== null &&
        this.suspendedFragmentRetries.has(fragmentRefetchKey(fragment)),
    );
    const retryCandidateKeys = new Set(retryCandidates.map(fragmentRefetchKey));
    for (const {rootQuery, refetchFragments} of rootQueriesToRefetch) {
      // Sharing the root document is not enough to take over a retry. A mounted
      // User(limit: 10) runner must not claim a suspended User(limit: 20) retry
      // just because the store also has render-only params for limit: 20.
      const mountedRetryKeys = new Set(
        this.getMatchingRootQueryParams(rootQuery, retryCandidates, {
          mountedOnly: true,
        })
          .flatMap(({fragments: matchedFragments}) => matchedFragments)
          .map(fragmentRefetchKey),
      );
      const fragmentsForCommittedRunner = fragments.filter((fragment) => {
        const key = fragmentRefetchKey(fragment);
        // Preserve ordinary committed refetch/invalidation dispatch. Only pairs
        // with a retained executor require a mounted variable match to displace it.
        return !retryCandidateKeys.has(key) || mountedRetryKeys.has(key);
      });
      if (fragmentsForCommittedRunner.length > 0) {
        // Remove fallbacks before dispatch so this batch uses the committed path
        // only. If that request also fails while suspended, its response can retain
        // a new executor with the context and params of that new request.
        fragmentsForCommittedRunner.forEach((fragment) => {
          const key = fragmentRefetchKey(fragment);
          if (this.suspendedFragmentRetries.has(key)) {
            this.suspendedFragmentRetries.delete(key);
          }
        });
        refetchFragments(fragmentsForCommittedRunner);
      }
    }

    // Only pairs not claimed by a committed runner still have entries. Batch by
    // executor so fragments from the same failed request can retry together,
    // without combining requests that carry different context or retained params.
    const retries = new Map<RefetchFragmentsFn, FragmentHashWithVariables[]>();
    for (const fragment of retryCandidates) {
      const key = fragmentRefetchKey(fragment);
      const retry = this.suspendedFragmentRetries.get(key);
      if (retry) {
        const batch = retries.get(retry.refetchFragments) ?? [];
        batch.push(fragment);
        retries.set(retry.refetchFragments, batch);
      }
    }
    for (const [refetchFragments, batch] of retries) {
      refetchFragments(batch);
    }
  }

  private addObservableQuery<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >(
    documentHashWithVariables: string,
    observableData: ObservableQuery<TDocumentNode>,
  ): void {
    this.observableQueryMap.set(documentHashWithVariables, observableData);

    if (this.observableQueryMap.size > MAX_STORE_SIZE) {
      evictOldest(this.observableQueryMap);
    }
  }

  private addObservableFragment<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >(
    documentHashWithVariables: string,
    observableData: ObservableFragment<TDocumentNode>,
  ): void {
    this.observableFragmentMap.set(documentHashWithVariables, observableData);

    if (this.observableFragmentMap.size > MAX_STORE_SIZE) {
      evictOldest(this.observableFragmentMap);
    }
  }

  private registerObservableDataOnTopLevelFields(
    topLevelFieldNodes: ReadonlyArray<FieldNode>,
    documentHash: string,
  ): void {
    const topLevelFieldNames = topLevelFieldNodes.map(
      (field) => field.name.value,
    );

    topLevelFieldNames.forEach((fieldName) => {
      const existingSet =
        this.topLevelFieldsToObservableHashesMap.get(fieldName);
      if (typeof existingSet === 'undefined') {
        this.topLevelFieldsToObservableHashesMap.set(
          fieldName,
          new Set([documentHash]),
        );
      } else {
        existingSet.add(documentHash);
      }
    });

    if (this.topLevelFieldsToObservableHashesMap.size > MAX_STORE_SIZE) {
      evictOldest(this.topLevelFieldsToObservableHashesMap);
    }
  }

  private getObservableHashesForTopLevelFields(
    topLevelFields: ReadonlyArray<ValidTopLevelField>,
  ): ReadonlyArray<string> {
    const nonDedupedTopLevelFields = topLevelFields.flatMap((field) => {
      const existingSet = this.topLevelFieldsToObservableHashesMap.get(field);
      if (typeof existingSet === 'undefined') {
        return [];
      }

      return Array.from(existingSet);
    });

    return Array.from(new Set(nonDedupedTopLevelFields));
  }
}

function triggerQueriesRefetch(
  observableQueries: ReadonlyArray<ObservableQuery<any>>,
): void {
  observableQueries.forEach((observableQuery) => {
    observableQuery.refetch();
  });
}
